import React, { useMemo } from "react";
import { Alert, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import styles from "./aiInsightsQuotaPanel.module.scss";

const { Text } = Typography;

/**
 * INFO: Shared quota summary for Dashboard AI Insights + Top Problems modals.
 * WARNING: Do not use antd theme.useToken() here — react-app-rewired may fail to resolve antd/es/theme/style.
 */
function AIInsightsQuotaPanel({
  quota,
  loading = false,
  error = false,
  thisRunOverride = null,
  isDark = false,
}) {
  const { t } = useTranslation();

  const fmt = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 0,
      }),
    [],
  );

  if (error && !quota) {
    return null;
  }

  if (loading && !quota) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          minHeight: 40,
        }}
      >
        <Spin size="small" />
        <Text
          type="secondary"
          className={`${styles.loadingText} ${isDark ? styles.textDarkMuted : ""}`}
        >
          {t("common.aiInsightsQuotaLoading")}
        </Text>
      </div>
    );
  }

  if (!quota || quota.error) {
    return null;
  }

  const rawThisRun =
    thisRunOverride != null
      ? thisRunOverride
      : quota.required_amount ?? quota.total_review_count ?? 0;
  const thisRun = Number.isFinite(Number(rawThisRun))
    ? Math.max(0, Math.round(Number(rawThisRun)))
    : 0;

  const unlimited = quota.unlimited === true;
  const current =
    quota.current_quota != null ? Math.max(0, Number(quota.current_quota)) : null;

  const after =
    unlimited || current == null
      ? null
      : Math.max(0, current - thisRun);

  const insufficient =
    !unlimited && current != null && thisRun > current;

  const wrapCls = [
    styles.wrap,
    isDark ? styles.wrapDark : "",
    insufficient ? styles.wrapInsufficient : "",
    insufficient && isDark ? styles.wrapInsufficientDark : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapCls}>
      <Text strong className={`${styles.title} ${isDark ? styles.titleDark : ""}`}>
        {t("common.aiInsightsQuotaStatusTitle")}
      </Text>

      <div className={styles.stats}>
        <div>
          <Text type="secondary" className={`${styles.statLabel} ${isDark ? styles.statLabelDark : ""}`}>
            {t("common.aiInsightsQuotaCurrent")}
          </Text>
          <Text strong className={`${styles.statValue} ${isDark ? styles.statValueDark : ""}`}>
            {unlimited ? t("common.aiInsightsQuotaUnlimited") : fmt.format(current ?? 0)}
          </Text>
        </div>
        <div>
          <Text type="secondary" className={`${styles.statLabel} ${isDark ? styles.statLabelDark : ""}`}>
            {t("common.aiInsightsQuotaThisRun")}
          </Text>
          <Text strong className={`${styles.statValue} ${isDark ? styles.statValueDark : ""}`}>
            {fmt.format(thisRun)}
          </Text>
        </div>
        <div>
          <Text type="secondary" className={`${styles.statLabel} ${isDark ? styles.statLabelDark : ""}`}>
            {t("common.aiInsightsQuotaAfter")}
          </Text>
          <Text strong className={`${styles.statValue} ${isDark ? styles.statValueDark : ""}`}>
            {unlimited ? "—" : fmt.format(after ?? 0)}
          </Text>
        </div>
      </div>

      {insufficient ? (
        <div className={isDark ? styles.alertDark : undefined}>
          <Alert
            type="warning"
            showIcon
            message={t("common.aiInsightsQuotaInsufficient")}
          />
        </div>
      ) : null}

      <Text
        type="secondary"
        className={`${styles.hint} ${isDark ? styles.hintDark : ""}`}
      >
        {t("common.aiInsightsQuotaHint")}
      </Text>
    </div>
  );
}

export default AIInsightsQuotaPanel;
